import { httpClient } from '../http/httpClient.js';

const FILE_EXPLORER_API_URL = '/api/file-explorer';
const FILE_EXPLORER_DOWNLOAD_URL = `${FILE_EXPLORER_API_URL}/download`;
// Contrato asumido para el modal de Mantenimiento (backup completo + borrado
// por período, ver mockups/explorador-archivos-mantenimiento/): estos cuatro
// endpoints todavía no existen en el backend al momento de escribir esto —
// se definen acá como referencia para que el equipo de backend los implemente;
// hasta entonces, estas llamadas devuelven 404.
const FILE_EXPLORER_MAINTENANCE_URL = `${FILE_EXPLORER_API_URL}/maintenance`;
const FILE_EXPLORER_BACKUP_URL = `${FILE_EXPLORER_API_URL}/backup`;
const FILE_EXPLORER_PERIOD_PREVIEW_URL = `${FILE_EXPLORER_API_URL}/period-preview`;
const FILE_EXPLORER_PERIOD_URL = `${FILE_EXPLORER_API_URL}/period`;

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
};

export const getFileExplorer = async ({
  folderId,
  sortBy = 'size',
  sortOrder = 'desc',
  search,
  // Sin default: se omiten de la query string cuando no se pasan (modo
  // grilla, sin paginación) en vez de caer en una página 0/20 implícita.
  pageCurrent,
  pageSize
} = {}) => {
  const queryString = buildQueryString({ folderId, sortBy, sortOrder, search, pageCurrent, pageSize });
  const url = queryString ? `${FILE_EXPLORER_API_URL}?${queryString}` : FILE_EXPLORER_API_URL;

  // skipAuthRedirect: un 403 de este endpoint (rol distinto de admin/operator) no debe
  // disparar el logout global de httpClient.js — se maneja inline (ver FileExplorerScreen).
  return httpClient.get(url, { skipAuthRedirect: true });
};

// Traduce un entry tal como lo devuelve `responses` del listado al shape que
// esperan los endpoints de acción (DELETE / download) — no requiere que el
// caller arme ids ni sepa la forma exacta del contrato.
const toExplorerApiItem = (entry) => (
  entry.type === 'folder'
    ? { id: entry.id, type: 'folder' }
    : { id: entry.id, type: 'file', source: entry.source }
);

export const deleteFileExplorerItems = async (entries) => {
  const items = entries.map(toExplorerApiItem);

  return httpClient.delete(FILE_EXPLORER_API_URL, {
    data: { items },
    skipAuthRedirect: true
  });
};

// parseAs: 'raw' porque la respuesta exitosa es un .zip en streaming (no
// JSON): se necesita el Response crudo para leer el header
// Content-Disposition y recién después pedirle el blob — a diferencia de
// parseAs: 'blob', que ya viene consumido y sin acceso a los headers.
export const downloadFileExplorerItems = async (entries) => {
  const items = entries.map(toExplorerApiItem);

  return httpClient.post(FILE_EXPLORER_DOWNLOAD_URL, {
    data: { items },
    parseAs: 'raw',
    skipAuthRedirect: true
  });
};

// GET → { lastBackup: { generatedAt, sizeBytes, fileCount } | null,
//         dateRange: { earliest, latest } (ISO, límites de la period bar) }
export const getFileExplorerMaintenanceInfo = async () => (
  httpClient.get(FILE_EXPLORER_MAINTENANCE_URL, { skipAuthRedirect: true })
);

// GET ?from&to (ISO) → { folders, files, sizeBytes } — usado para el contador
// en vivo de la period bar; se llama debounced, no en cada pixel de arrastre.
export const getFileExplorerPeriodPreview = async ({ from, to }) => {
  const queryString = buildQueryString({ from, to });
  return httpClient.get(`${FILE_EXPLORER_PERIOD_PREVIEW_URL}?${queryString}`, { skipAuthRedirect: true });
};

// POST sin body → igual que downloadFileExplorerItems, respuesta cruda (.zip
// en streaming) con Content-Disposition; backup de todo el árbol, no de una
// selección.
export const generateFileExplorerBackup = async () => (
  httpClient.post(FILE_EXPLORER_BACKUP_URL, { parseAs: 'raw', skipAuthRedirect: true })
);

// DELETE { from, to } (ISO) → { deletedFolders, deletedFiles } — borra por
// rango de fechas en todo el árbol, no por selección de ids puntuales.
export const deleteFileExplorerByPeriod = async ({ from, to }) => (
  httpClient.delete(FILE_EXPLORER_PERIOD_URL, {
    data: { from, to },
    skipAuthRedirect: true
  })
);

export const fileExplorerService = {
  getFileExplorer,
  deleteFileExplorerItems,
  downloadFileExplorerItems,
  getFileExplorerMaintenanceInfo,
  getFileExplorerPeriodPreview,
  generateFileExplorerBackup,
  deleteFileExplorerByPeriod
};
